import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import type { AccountInfo, PaginationParams, PaginationResponse } from '@/types'
import apiClient from '@/lib/api/client'
import { 
  getAccountOverview, 
  getTransactions, 
  selfRecharge,
  tenantGetBalance as getAccountBalance, 
  listBillingRecords, 
  exportBilling, 
  downloadBilling 
} from '@/lib/api/generated/sdk.gen'

export interface RechargeRecord {
  id: string
  amount: number
  method: string
  status: 'SUCCESS' | 'PENDING' | 'FAILED'
  createdAt: string
  transactionId: string
}

export interface ConsumptionRecord {
  id: string
  type: 'SMS' | 'TEMPLATE' | 'SIGNATURE'
  description: string
  amount: number
  balance: number
  createdAt: string
}

export interface AccountOverview {
  tenantId: number
  balance: number
  frozenBalance: number
  availableBalance: number
  totalRecharge: number
  totalConsumption: number
  totalSmsSent: number
  status: string
  lastUpdateTime: string
}

export interface TransactionRecord {
  id: number
  transactionNo: string
  transactionType: string
  amount: number
  balanceBefore: number
  balanceAfter: number
  description: string
  paymentMethod: string
  createdAt: string
}

// 获取账户概览
export const useAccountOverview = () => {
  return useQuery({
    queryKey: ['account', 'overview'],
    queryFn: async (): Promise<AccountOverview> => {
      try {
        // 使用余额API获取账户信息
        const balanceResult = await getAccountBalance({ 
          client: apiClient, 
          responseStyle: 'fields', 
          throwOnError: false 
        })
        console.log('Balance API Response:', balanceResult)
        
        const balanceData = (balanceResult as any)?.data
        // API返回的是currentBalance而不是balance
        const balance = Number(balanceData?.currentBalance) || 0
        const frozenBalance = Number(balanceData?.frozenBalance) || 0
        const availableBalance = Number(balanceData?.availableBalance) || 0
        const totalRecharge = Number(balanceData?.totalRecharge) || 0
        const totalConsumption = Number(balanceData?.totalConsumption) || 0
        const totalSmsSent = Number(balanceData?.totalSmsSent) || 0
        const status = balanceData?.status || 'ACTIVE'
        const lastUpdateTime = balanceData?.lastUpdateTime || new Date().toISOString()
        
        // 获取费用明细来统计充值消费
        const billingResult = await listBillingRecords({ 
          client: apiClient, 
          responseStyle: 'fields', 
          throwOnError: false,
          query: { page: 1, size: 1000 } // 获取所有记录用于统计
        })
        
        const billingData = (billingResult as any)?.data
        const records = billingData?.records || []
        
        // 统计充值总额和消费总额
        let calculatedTotalRecharge = 0
        let calculatedTotalConsumption = 0

        records.forEach((record: any) => {
          const amount = Number(record.amount) || 0
          if (record.type === 'RECHARGE') {
            calculatedTotalRecharge += amount
          } else if (record.type === 'CONSUMPTION') {
            calculatedTotalConsumption += amount
          }
        })
        
        const processedData = {
          tenantId: 1222, // 固定租户ID
          balance: balance, // 使用API返回的currentBalance
          frozenBalance: frozenBalance, // 使用API返回的frozenBalance
          availableBalance: availableBalance, // 使用API返回的availableBalance
          totalRecharge: totalRecharge, // 使用API返回的totalRecharge
          totalConsumption: totalConsumption, // 使用API返回的totalConsumption
          totalSmsSent: totalSmsSent, // 使用API返回的totalSmsSent
          status: status, // 使用API返回的status
          lastUpdateTime: lastUpdateTime, // 使用API返回的lastUpdateTime
        }

        // 如果API返回的数据为空，使用计算的数据作为后备
        if (totalRecharge === 0 && calculatedTotalRecharge > 0) {
          processedData.totalRecharge = calculatedTotalRecharge
        }
        if (totalConsumption === 0 && calculatedTotalConsumption > 0) {
          processedData.totalConsumption = calculatedTotalConsumption
        }
        console.log('Final Account Data:', processedData)
        return processedData
        
      } catch (error) {
        console.error('Error fetching account overview:', error)
        // 返回默认值
        return {
          tenantId: 1222,
          balance: 0,
          frozenBalance: 0,
          availableBalance: 0,
          totalRecharge: 0,
          totalConsumption: 0,
          totalSmsSent: 0,
          status: 'ACTIVE',
          lastUpdateTime: new Date().toISOString(),
        }
      }
    },
  })
}

// 获取账户信息
export const useAccountInfo = () => {
  return useQuery({
    queryKey: ['account', 'info'],
    queryFn: async (): Promise<AccountInfo> => {
      const res: any = await getAccountBalance({ client: apiClient, responseStyle: 'fields', throwOnError: false })
      const data = (res as any)?.data ?? res
      // 适配返回结构，至少包含 balance 字段
      const balanceValue = (data?.balance as number) ?? (data?.data?.balance as number) ?? 0
      return {
        tenantId: '',
        tenantName: '',
        balance: balanceValue,
        creditLimit: 0,
        usedCredit: 0,
        status: 'ACTIVE',
      }
    },
  })
}

// 获取交易记录
export const useTransactionRecords = (params: PaginationParams & { transactionType?: string; startDate?: string; endDate?: string }) => {
  return useQuery({
    queryKey: ['account', 'transactions', params],
    queryFn: async (): Promise<PaginationResponse<TransactionRecord>> => {
      const query: any = {
        page: params.page + 1, // 后端从1开始
        size: params.size,
      }
      
      if (params.startDate) {
        query.startDate = params.startDate
      }
      if (params.endDate) {
        query.endDate = params.endDate
      }
      
      // 使用正确的费用明细API
      const result = await listBillingRecords({ 
        client: apiClient, 
        responseStyle: 'fields', 
        throwOnError: false,
        query 
      })
      
      // 正确提取数据
      const responseData = (result as any)?.data
      console.log('Billing Records Response Data:', responseData)
      
      // 转换数据格式以匹配TransactionRecord接口
      const records: TransactionRecord[] = (responseData?.records || []).map((item: any) => ({
        id: item.id,
        transactionNo: `TXN_${item.id}`, // 生成交易单号
        transactionType: item.type,
        amount: Number(item.amount),
        balanceBefore: 0, // 后端暂未提供
        balanceAfter: 0, // 后端暂未提供
        description: item.description || '',
        paymentMethod: 'SYSTEM', // 默认值
        createdAt: item.createdAt,
      }))
      
      return {
        records: records,
        total: responseData?.total || 0,
        pages: responseData?.totalPages || 1,
        size: params.size,
        current: params.page + 1,
      }
    },
  })
}

// 获取充值记录
export const useRechargeRecords = (params: PaginationParams) => {
  return useQuery({
    queryKey: ['account', 'recharge', params],
    queryFn: async (): Promise<PaginationResponse<RechargeRecord>> => {
      // 暂无直接充值记录接口，先复用账单记录作为替代
      const res: any = await listBillingRecords({ client: apiClient, responseStyle: 'fields', throwOnError: false, query: { page: params.page, size: params.size } })
      const payload: any = (res as any)?.data ?? res
      const records: RechargeRecord[] = (payload?.records || payload?.content || []).map((it: any) => ({
        id: String(it.id ?? it.transactionId ?? ''),
        amount: Number(it.amount ?? 0),
        method: String(it.method ?? it.type ?? 'UNKNOWN'),
        status: (it.status as any) || 'SUCCESS',
        createdAt: String(it.createdAt ?? it.timestamp ?? new Date().toISOString()),
        transactionId: String(it.transactionId ?? it.id ?? ''),
      }))
      return {
        records: records,
        total: payload?.total ?? payload?.totalElements ?? records.length,
        pages: payload?.totalPages ?? 1,
        size: params.size,
        current: params.page + 1,
      }
    },
  })
}

// 获取消费记录
export const useConsumptionRecords = (params: PaginationParams) => {
  return useQuery({
    queryKey: ['account', 'consumption', params],
    queryFn: async (): Promise<PaginationResponse<ConsumptionRecord>> => {
      const res: any = await listBillingRecords({ client: apiClient, responseStyle: 'fields', throwOnError: false, query: { page: params.page, size: params.size } })
      const payload: any = (res as any)?.data ?? res
      const records: ConsumptionRecord[] = (payload?.records || payload?.content || []).map((it: any) => ({
        id: String(it.id ?? ''),
        type: (it.type as any) || 'SMS',
        description: String(it.description ?? it.remark ?? ''),
        amount: Number(it.amount ?? 0),
        balance: Number(it.balanceAfter ?? it.balance ?? 0),
        createdAt: String(it.createdAt ?? it.timestamp ?? new Date().toISOString()),
      }))
      return {
        records: records,
        total: payload?.total ?? payload?.totalElements ?? records.length,
        pages: payload?.totalPages ?? 1,
        size: params.size,
        current: params.page + 1,
      }
    },
  })
}

// 发起充值
export const useCreateRecharge = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: { amount: number; description?: string; paymentMethod?: string }): Promise<{ success: boolean; message: string }> => {
      const result = await selfRecharge({ 
        client: apiClient, 
        responseStyle: 'fields', 
        throwOnError: false,
        body: data as any
      })
      const response = (result as any)?.data ?? result
      return response
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account'] })
    },
  })
}